import React, { useEffect, useState } from 'react';
import {
  Button, Radio, RadioGroup,
  Modal, ModalHeader, ModalContent, ModalFooter, ModalButtons,
} from 'pvs-design-system';
import { Box } from '@mui/material';
import {
  type SearchInValue, SEARCH_IN_OPTIONS, type SearchMode, SEARCH_MODE_LABELS,
} from './LibrarySearchBar';

interface AdvancedSearchFiltersModalProps {
  open: boolean;
  onClose: () => void;
  searchIn: SearchInValue;
  onSearchInChange: (v: SearchInValue) => void;
  searchMode: SearchMode;
  onSearchModeChange: (v: SearchMode) => void;
}

/** Modal version of the search bar's "Advanced search" filters — two sections,
 *  Search in and Match mode. Shared by every page that renders the hero search bar.
 *  Selections are staged locally and only committed when "Apply filters" is clicked. */
const AdvancedSearchFiltersModal: React.FC<AdvancedSearchFiltersModalProps> = ({
  open, onClose, searchIn, onSearchInChange, searchMode, onSearchModeChange,
}) => {
  const [pendingSearchIn, setPendingSearchIn] = useState(searchIn);
  const [pendingSearchMode, setPendingSearchMode] = useState(searchMode);

  useEffect(() => {
    if (open) {
      setPendingSearchIn(searchIn);
      setPendingSearchMode(searchMode);
    }
  }, [open, searchIn, searchMode]);

  const handleApply = () => {
    onSearchInChange(pendingSearchIn);
    onSearchModeChange(pendingSearchMode);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabelledby="advanced-search-title"
      sx={{ '& .MuiDialog-paper': { width: 600 } }}
    >
      <ModalHeader id="advanced-search-title" title="Advanced search" onClose={onClose} />
      <ModalContent>
        <Box sx={{ mb: 3 }}>
          <RadioGroup
            id="advanced-search-in"
            label="Search in"
            value={pendingSearchIn}
            onChange={(e) => setPendingSearchIn(e.target.value as SearchInValue)}
          >
            {SEARCH_IN_OPTIONS.map((opt) => (
              <Radio key={opt.value} id={`search-in-${opt.value}`} label={opt.label} value={opt.value} />
            ))}
          </RadioGroup>
        </Box>

        <Box>
          <RadioGroup
            id="advanced-search-mode"
            label="Match mode"
            value={pendingSearchMode}
            onChange={(e) => setPendingSearchMode(e.target.value as SearchMode)}
          >
            {SEARCH_MODE_LABELS.map((mode) => (
              <Radio
                key={mode.value}
                id={`search-mode-${mode.value}`}
                label={mode.label}
                value={mode.value}
                helperText={mode.description}
                helperTextProps={{ sx: { ml: '30px', mt: 0, mb: '4px' } }}
              />
            ))}
          </RadioGroup>
        </Box>
      </ModalContent>
      <ModalFooter>
        <ModalButtons>
          <Button variant="text" color="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleApply}>Apply filters</Button>
        </ModalButtons>
      </ModalFooter>
    </Modal>
  );
};

export default AdvancedSearchFiltersModal;
